import { Component, DestroyRef, inject, input, OnInit, output, signal, ChangeDetectionStrategy } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { finalize } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CustomerService } from '../../services/customer.service';
import { OtpComponent } from '../otp/otp.component';
import { AuthService } from '../../services/auth.service';
import type { OtpConfirmDto, OtpRequestDto, ResetPasswordConfirmDto } from '@api/models';
import { CustomValidators } from '../../validators/validators';
import { OTP_LENGTH } from '../../constants';
import { ButtonComponent } from '../button/button.component';
import { resolveHttpErrorMessage } from '@/helpers/resolve-http-error-message.helper';


class ResetPasswordForm {
  login: FormControl<string>;
  password: FormControl<string>;
  repeatPassword: FormControl<string>;
  otpDigits: FormArray<FormControl<string>>;
}

type ResetPasswordFormState = 'otp' | 'password' | 'success';

@Component({
  selector: 'tc-reset-password',
  templateUrl: './reset-password.component.html',
  styleUrls: ['./reset-password.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    TranslatePipe,
    OtpComponent,
    FormsModule,
    ReactiveFormsModule,
    ButtonComponent,
  ],
})
export class ResetPasswordComponent implements OnInit {
  customerService = inject(CustomerService);
  private destroyRef = inject(DestroyRef);
  private authService = inject(AuthService);

  login = input.required<string>();
  success = output();

  state = signal<ResetPasswordFormState>('otp');
  isLoading = signal<boolean>(false);
  error = signal<string>(null);
  otpRequest = signal<OtpRequestDto>(null);
  private isFormErrorsVisible = signal<boolean>(false);

  form: FormGroup<ResetPasswordForm>;

  ngOnInit(): void {
    this.buildForm();
  }

  private buildForm(): void {
    const passwordControl = new FormControl<string>('', Validators.required);

    this.form = new FormGroup({
      login: new FormControl<string>(this.login(), Validators.required),
      password: passwordControl,
      repeatPassword: new FormControl<string>('', CustomValidators.passwordConfirm(passwordControl)),
      otpDigits: new FormArray(
        new Array(OTP_LENGTH).fill(null).map(() => new FormControl('', Validators.required)),
      ),
    });

    this.form.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.resetErrors());

    this.resetPasswordInit();
  }

  submit(): void {
    if (this.isLoading()) {
      return;
    }

    this.resetErrors();

    switch (this.state()) {
      case 'otp':
        this.resetPasswordOtp();
        break;
      case 'password':
        this.resetPasswordConfirm();
        break;
      case 'success':
        this.success.emit();
        break;
    }
  }

  resetPasswordInit(): void {
    this.resetErrors();
    if (this.form.controls.login.invalid) {
      this.isFormErrorsVisible.set(true);
      return;
    }

    this.isLoading.set(true);
    this.authService.resetPasswordInit(this.form.controls.login.getRawValue())
      .pipe(
        finalize(() => this.isLoading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: response => {
          this.otpRequest.set(response);
          this.resetErrors();
          this.state.set('otp');
        },
        error: error => this.error.set(resolveHttpErrorMessage(error)),
      });
  }

  resetPasswordOtp(): void {
    this.resetErrors();
    if (this.form.controls.otpDigits.invalid) {
      this.isFormErrorsVisible.set(true);
      return;
    }

    const dto: OtpConfirmDto = {
      otpCode: this.form.controls.otpDigits.value.join(''),
      requestId: this.otpRequest().requestId,
    };

    this.isLoading.set(true);
    this.authService.resetPasswordOtp(dto)
      .pipe(
        finalize(() => this.isLoading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          this.resetErrors();
          this.state.set('password');
        },
        error: error => this.error.set(resolveHttpErrorMessage(error)),
      });
  }

  resetPasswordConfirm(): void {
    this.resetErrors();
    if (this.form.controls.password.invalid || this.form.controls.repeatPassword.invalid) {
      this.isFormErrorsVisible.set(true);
      return;
    }

    const dto: ResetPasswordConfirmDto = {
      requestId: this.otpRequest().requestId,
      password: this.form.controls.password.getRawValue(),
    };

    this.isLoading.set(true);
    this.authService.resetPasswordConfirm(dto)
      .pipe(
        finalize(() => this.isLoading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => this.state.set('success'),
        error: error => this.error.set(resolveHttpErrorMessage(error)),
      });
  }

  isControlInvalid(control: AbstractControl): boolean {
    return this.isFormErrorsVisible() && control.invalid;
  }

  resetErrors(): void {
    this.isFormErrorsVisible.set(false);
    this.error.set(null);
  }
}
